define((require) => {
    "use strict";

    const ImageWidgetModel              = require("shared/ui/experience/widgets/imageWidget/ImageWidgetModel");
    const ImageWidgetView               = require("shared/ui/experience/widgets/imageWidget/ImageWidgetView");
    const ReactBridge                   = require("backbone.react-bridge");
    const ImageWidgetComponent          = require("ui/experience/widgets/imageWidget/ImageWidgetComponent").default;
    const ImageWidgetSizeCalculations   = require("ui/experience/widgets/imageWidget/helpers/ImageWidgetSizeCalculations.ts");
    const Marionette                    = require("marionette");
    const ImageURLProvider              = require("shared/ui/experience/widgets/imageWidget/ImageURLProvider");
    const LoggedInUserState             = require("shared/ui/user/LoggedInUserState");
    const ImageLibrarySourceEnum        = require("shared/data/images/ImageLibrarySourceEnum");
    const EditorEventFilters            = require("ui/editor/EditorEventFilters").default;
    const UUIDGenerator                 = require("ui/experience/widgets/UUIDGenerator");
    const EditorActivityManager         = require("editor/ui/experience/editor/activity/EditorActivityManager");


    /**
     * @class {ImageWidgetViewEditorMixin}
     *
     * The Image widget view Editor Mixin rendering an image widget.
     *
     * @cfg {ImageWidgetModel} model The model of the image widget being rendered.
     */
    const EnhancedImageWidgetLayout = Marionette.Layout.extend({

        template: () => "<div class=\"image-widget-layout-container\"></div>",

        regions: {
            "imageWidget": ".image-widget-layout-container"
        }
    });

    return ImageWidgetView.mixin("ImageWidgetViewEditorMixin", {

        targetTransformer: null,

        widgetSelectedListener: null,

        imageSizes: null,

        imageCoords: null,

        _reactImageComponent: null,

        // This gives us access to the proper this context
        getPropsFromView: (self) =>
            ({
                onCropStart: () => {
                    EditorActivityManager.setBatchActivityId(UUIDGenerator.createUUID());
                    self.setDragDisplay.call(self, false);
                },
                onCropEnd: () => {
                    self.setDragDisplay.call(self, true);
                    EditorActivityManager.setBatchActivityId(null);
                    // We may actually need to store a reference to the previously selected widgets. Not sure how we'll
                    // want to detect this just yet though.
                    self.targetTransformer.trigger("setSelectedWidgets", [self]);
                },
                onSave: (cropCoords) => self.onCropCoordsSave.call(self, cropCoords)
            }),

        getSelectedWidgets(widgets) {
            const isSingleSelection = widgets.length === 1;
            let isCroppable = false;
            if (isSingleSelection) {
                const selectedWidget = widgets[0].model;
                const isSceneNavigationWidget =
                    selectedWidget.has("action") && selectedWidget.get("action").get("type") === "sceneNavigation";
                const selectedWidgetUUID = selectedWidget.get("uuid");
                const isSelected = this.model.get("uuid") === selectedWidgetUUID;

                isCroppable = isSelected && !isSceneNavigationWidget;
            }

            this.model.set("isCroppable", isCroppable, {cause: EditorEventFilters.SUPERFICIAL});
        },

        onShow() {
            if (!this.widgetSelectedListener && this.parentView) {
                const targetView = this.parentView.options.parentCollectionView || this.parentView;
                this.targetTransformer = targetView.transformer;
                if (this.targetTransformer) {
                    this.widgetSelectedListener = this.listenTo(
                        this.targetTransformer,
                        "setSelectedWidgets",
                        this.getSelectedWidgets
                    );
                }
            }

            this.__super();
            // Creating container element to append image to it
            this._layout = new EnhancedImageWidgetLayout({el: this.$el});
            this._layout.render();
            // Creating React Component
            const ImageWidgetReactComponent = ReactBridge.viewFromComponent(ImageWidgetComponent, {
                model: this.model,
                viewModel: this.viewModel,
                observe: {
                    model: "change"
                },
                props: this.getPropsFromView(this),
                getProps({model, viewModel}) {
                    let src = null;
                    // Check if crop enabled
                    if (model.get("src") && model.get("isDragAvailable") === false) {
                        src = ImageURLProvider.getOriginalImageUrl(model.get("src"), ImageLibrarySourceEnum.CREATOR);
                    } else {
                        src = ImageURLProvider.provide(model);
                    }

                    const imageCroppingEnabled = LoggedInUserState.get("companySettings").get("isImageCroppingEnabled");
                    // Calculate original image size according to scene (in percents)
                    const originalImageSceneRelativeSizes =
                        ImageWidgetSizeCalculations.calculateOriginalImageSceneRelativeSize(model, viewModel.get("experience"));
                    const isDefaultImage = src === ImageWidgetModel.DEFAULT_IMAGE_URL;
                    const crop = model.get("crop");
                    return {
                        src,
                        originalImageSceneRelativeSizes,
                        crop,
                        canCrop: model.get("isCroppable") && imageCroppingEnabled && !isDefaultImage
                    };
                }
            });

            this._reactImageComponent = ImageWidgetReactComponent;
            // Appending React component to backbone view
            this._layout.imageWidget.show(ImageWidgetReactComponent);
        },

        setDragDisplay(dragEnabled) {
            const display = dragEnabled ? "initial" : "none";
            const controlsContainerList = document.getElementsByClassName("controls-container");
            for (let i = 0; i < controlsContainerList.length; i++) {
                controlsContainerList[i].style.display = display;
            }
            this.model.set("isDragAvailable", dragEnabled);

            if (!dragEnabled && this.model.has("crop")) {
                this.viewModel.set("preventSelectionChange", true);
                this.imageSizes = ImageWidgetSizeCalculations.calculateOriginalImageSceneRelativeSize(this.model, this.viewModel.get("experience"));
                this.imageCoords = ImageWidgetSizeCalculations.calculateWidgetCoords(this.model, this.imageSizes.width, this.imageSizes.height);
                // Increase image widget size to initial image
                // But do not change image sizes in model
                this.el.style.width = `${this.imageSizes.width}px`;
                this.el.style.height = `${this.imageSizes.height}px`;
                this.el.style.top = `-${this.imageCoords.y}px`;
                this.el.style.left = `-${this.imageCoords.x}px`;
            } else {
                this.viewModel.set("preventSelectionChange", false);
                this.el.style.top = "initial";
                this.el.style.left = "initial";
            }
        },

        /* global WebKitCSSMatrix */
        onCropCoordsSave(cropCoords) {
            let widthFixCropCoords = cropCoords;
            const prevCropCoords = this.model.get("crop");
            const imageWidth = this.model.get("width");
            const imageHeight = this.model.get("height");

            // Cloudinary width and height 1 fix
            // https://jira.zmags.com/browse/DEV-2211?focusedCommentId=67856&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-67856
            if (widthFixCropCoords.width >= 1) {
                widthFixCropCoords.width = Number(1).toFixed(1);
            }

            if (widthFixCropCoords.height >= 1) {
                widthFixCropCoords.height = Number(1).toFixed(1);
            }

            if (prevCropCoords) {
                const originalImageWidth = imageWidth / prevCropCoords.width;
                const originalImageHeight = imageHeight / prevCropCoords.height;
                // Crop of the already cropped image
                // Adapting crop coords of cropped piece to the original image, which is used to crop
                this.model.setSizeOfImage(this.options.viewModel.get("experience"), widthFixCropCoords.width, widthFixCropCoords.height, originalImageWidth, originalImageHeight);
                /* Get full image position on canvas */
                const fullImageOffsetX = new WebKitCSSMatrix(this.el.style.transform).m41 - this.imageCoords.x;
                const fullImageOffsetY = new WebKitCSSMatrix(this.el.style.transform).m42 - this.imageCoords.y;
                this.model.setPositionOfCroppedImage(
                    widthFixCropCoords,
                    null,
                    null,
                    this.imageSizes,
                    this.options.viewModel.get("experience"),
                    fullImageOffsetX,
                    fullImageOffsetY
                );
            } else {
                // First image crop
                this.model.setSizeOfImage(this.options.viewModel.get("experience"), widthFixCropCoords.width, widthFixCropCoords.height, imageWidth, imageHeight);
                this.model.setPositionOfCroppedImage(widthFixCropCoords, imageWidth, imageHeight);
            }
            this.model.set("crop", widthFixCropCoords, imageWidth, imageHeight);
            this.viewModel.set("preventSelectionChange", false);
            this.el.style.top = "initial";
            this.el.style.left = "initial";
        },

        updateRendering(element, bounds, active, edited, changedProperties) {
            if (edited && changedProperties && this._source === ImageWidgetModel.DEFAULT_IMAGE_URL) {
                if (changedProperties.imageUrl) {
                    this.model.initializeSizeOfImage(this.options.viewModel.get("experience"));
                    if (this.model.get("scaleOnInitialize")) {
                        this.model.set("left", this.model.get("scaledWidgetWidth") / 2 - this.model.get("scaledWidth") / 2);
                        this.model.set("top", this.model.get("scaledWidgetHeight") / 2 - this.model.get("scaledHeight") / 2);
                    }
                }
            }

            if (this.model.get("sourceType")) {
                this._imageServingUrl = this._getImageServingUrl();
            }

            const resource = this.model.get("imageUrl");
            const sizeChanged = changedProperties && (changedProperties.scaledWidth || changedProperties.scaledHeight);

            if (this._source !== resource || sizeChanged) {
                const provideOptions = {bounds};

                if (this.model.get("sourceType")) {
                    provideOptions.imageServingUrl = this._imageServingUrl;
                }
                // Update image url in model
                this.model.set("imageUrl", resource);
                // Create full link of the image
                this._currentUrl = ImageURLProvider.provide(this.model, provideOptions);
                this._source = this._currentUrl;
            }

            this.__super();
        },

        _renderOriginalImage() {
            // Killing this method since we'll always want the imageUrl from the model when rendering.
        },

        /**
         * This method is extends and changes the highlight
         * of the widget astract method `_updateHightlight`
         */
        applyWidgetSpecificHighlight() {
            // Specific highlight only for navigation arrows
            if (this.model.get("action") && this.model.get("action").get("type") === "sceneNavigation" && !this.model.get("src")) {
                // Highlight widget if required
                if (this.model.get("highlighted")) {
                    this.$el.css({
                        background: "rgba(255,255,255,0.75)",
                        boxShadow: "inset 0px 0px 0px 3px #8c01ff"
                    });
                } else if (!this.model.get("boxShadow")) {
                    this.$el.css({
                        boxShadow: "inset 0px 0px 0px 0px transparent"
                    });
                }
            }
        },

        onClose() {
            if (this._reactImageComponent) {
                this._reactImageComponent.close();
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/imageWidget/ImageWidgetViewEditorMixin.js